import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import styled from 'styled-components';
import { FaCheck, FaPen, FaCog, FaSuitcase } from 'react-icons/fa';
import { useModal } from 'store/modal';
import { useActiveClientContext } from 'store/active-client';
import { buildClientEntity } from 'utils/helpers/client';
import handlePreviewDoc from 'utils/helpers/handlePreviewDoc';
import API from 'core/Api';
import { createErrorFromResponse } from 'core/Error';
import Button from 'components/Button';
import TooltipWrapper from 'components/TooltipWrapper';
import { multiSignDocAsEmployer } from 'routes/client/documents/helpers';
import LeavesCalendarGrid, { ILeaveData, IWorkTripData } from 'components/leavescalendargrid';
import { getEventStyle as getWorkTripsEventStyle } from 'utils/helpers/work-trips';
import RejectLeave from './RejectLeave';

interface IProps {
  leavesData: ILeaveData[];
  holidaysData: any[];
  workTripsData?: IWorkTripData[];
  date: Date;
  onDateChange?: (newDate: Date) => void;
}

const LeavesGrid: FC<IProps> = ({ leavesData, holidaysData, workTripsData, date, onDateChange }) => {
  const { t } = useTranslation();
  const { client } = useActiveClientContext();
  const { showModal, hideModal } = useModal();
  const queryClient = useQueryClient();

  const [actionIndex, setActionIndex] = useState<string | null>(null);
  const [isApproving, setIsApproving] = useState(false);
  const [isSigning, setIsSigning] = useState(false);
  const [isApprovingAndSigning, setIsApprovingAndSigning] = useState(false);
  const [isCompactActions, setIsCompactActions] = useState(window.innerWidth < 1400);

  useEffect(() => {
    const handleResize = () => {
      setIsCompactActions(window.innerWidth < 1400);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Approve leave function
  const approveLeave = (leaveId: number) => {
    return API.post(`leaves/${leaveId}/approve`)
      .then(res => res.data)
      .then(() => {
        queryClient.resetQueries(buildClientEntity('leaves', client));
        showModal({
          type: 'success',
          heading: t('common.success'),
          content: t('leaves.successfullyApproved'),
        });
      })
      .catch(err =>
        showModal({
          type: 'error',
          content: createErrorFromResponse(err).message,
        })
      );
  };

  // Sign leave function
  const signLeave = (leaveId: number) => {
    return API.get(`leaves/${leaveId}/documents-holders`)
      .then(res => res.data)
      .then(({ request_document_holder_id: reqDocHolderId, order_document_holder_id: orderDocHolderId }) => {
        multiSignDocAsEmployer(
          [reqDocHolderId, orderDocHolderId],
          showModal,
          () => queryClient.resetQueries(buildClientEntity('leaves', client)),
          '',
          t
        );
      })
      .catch(err =>
        showModal({
          type: 'error',
          content: createErrorFromResponse(err).message,
        })
      );
  };

  // Approve and sign leave function
  const approveAndSignLeave = (leaveId: number) => {
    return API.post(`leaves/${leaveId}/approve`)
      .then(res => res.data)
      .then(({ request_document_holder_id: reqDocHolderId, order_document_holder_id: orderDocHolderId }) => {
        multiSignDocAsEmployer(
          [reqDocHolderId, orderDocHolderId],
          showModal,
          () => queryClient.resetQueries(buildClientEntity('leaves', client)),
          '',
          t
        );
      })
      .catch(err =>
        showModal({
          type: 'error',
          content: createErrorFromResponse(err).message,
        })
      );
  };

  const executeApprove = (leaveId: number, cellKey: string, closeModal?: boolean) => {
    if (closeModal) hideModal();
    setActionIndex(cellKey);
    setIsApproving(true);
    approveLeave(leaveId).then(() => {
      setActionIndex(null);
      setIsApproving(false);
    });
  };

  const executeSign = (leaveId: number, cellKey: string, closeModal?: boolean) => {
    if (closeModal) hideModal();
    setActionIndex(cellKey);
    setIsSigning(true);
    signLeave(leaveId).then(() => {
      setActionIndex(null);
      setIsSigning(false);
    });
  };

  const executeApproveAndSign = (leaveId: number, cellKey: string, closeModal?: boolean) => {
    if (closeModal) hideModal();
    setActionIndex(cellKey);
    setIsApprovingAndSigning(true);
    approveAndSignLeave(leaveId).then(() => {
      setActionIndex(null);
      setIsApprovingAndSigning(false);
    });
  };

  const renderActionButton = (
    mode: 'grid' | 'modal',
    button: { type: string; text: string },
    idx: number,
    leaveId: number,
    requestDocId: number,
    day: number
  ) => {
    const { type, text } = button;
    const cellKey = `${leaveId}-${day}`;

    if (type === 'approve') {
      if (mode === 'grid')
        return (
          <TooltipWrapper
            key={`approve-${idx}`}
            id={`tooltip-approve-${cellKey}`}
            overlay={text}
          >
            <Button
              type="button"
              btnType="icon"
              size="sm"
              disabled={isApproving && actionIndex === cellKey}
              loading={isApproving && actionIndex === cellKey}
              onClick={e => {
                e.stopPropagation();
                executeApprove(leaveId, cellKey);
              }}
              className="leaves-grid-action-btn"
            >
              <FaCheck
                color="#32bfa3"
                size={12}
              />
            </Button>
          </TooltipWrapper>
        );

      return (
        <Button
          key={`approve-modal-${idx}`}
          type="button"
          btnType="success"
          className="leaves-modal-action-button"
          disabled={isApproving && actionIndex === cellKey}
          loading={isApproving && actionIndex === cellKey}
          onClick={() => executeApprove(leaveId, cellKey, true)}
        >
          <FaCheck size={14} />
          <span>{text}</span>
        </Button>
      );
    }

    if (type === 'sign') {
      if (mode === 'grid')
        return (
          <TooltipWrapper
            key={`sign-${idx}`}
            id={`tooltip-sign-${cellKey}`}
            overlay={text}
          >
            <Button
              type="button"
              btnType="icon"
              size="sm"
              disabled={isSigning && actionIndex === cellKey}
              loading={isSigning && actionIndex === cellKey}
              onClick={e => {
                e.stopPropagation();
                executeSign(leaveId, cellKey);
              }}
              className="leaves-grid-action-btn"
            >
              <FaPen
                color="#4492db"
                size={12}
              />
            </Button>
          </TooltipWrapper>
        );

      return (
        <Button
          key={`sign-modal-${idx}`}
          type="button"
          btnType="info"
          className="leaves-modal-action-button"
          disabled={isSigning && actionIndex === cellKey}
          loading={isSigning && actionIndex === cellKey}
          onClick={() => executeSign(leaveId, cellKey, true)}
        >
          <FaPen size={14} />
          <span>{text}</span>
        </Button>
      );
    }

    if (type === 'approve_and_sign') {
      if (mode === 'grid')
        return (
          <TooltipWrapper
            key={`approve-sign-${idx}`}
            id={`tooltip-approve-sign-${cellKey}`}
            overlay={text}
          >
            <Button
              type="button"
              btnType="icon"
              size="sm"
              disabled={isApprovingAndSigning && actionIndex === cellKey}
              loading={isApprovingAndSigning && actionIndex === cellKey}
              onClick={e => {
                e.stopPropagation();
                executeApproveAndSign(leaveId, cellKey);
              }}
              className="leaves-grid-action-btn"
            >
              <FaCheck
                color="#4492db"
                size={12}
              />
            </Button>
          </TooltipWrapper>
        );

      return (
        <Button
          key={`approve-sign-modal-${idx}`}
          type="button"
          btnType="info"
          className="leaves-modal-action-button"
          disabled={isApprovingAndSigning && actionIndex === cellKey}
          loading={isApprovingAndSigning && actionIndex === cellKey}
          onClick={() => executeApproveAndSign(leaveId, cellKey, true)}
        >
          <FaCheck size={14} />
          <span>{text}</span>
        </Button>
      );
    }

    if (type === 'reject') {
      if (mode === 'grid')
        return (
          <TooltipWrapper
            key={`reject-${idx}`}
            id={`tooltip-reject-${cellKey}`}
            overlay={text}
          >
            <RejectLeave
              leaveId={leaveId}
              buttonText={text}
              iconOnly
              noTooltip
              wrapperClassName="d-inline-block"
              buttonClassName="leaves-grid-action-btn"
              handlePreview={() => handlePreviewDoc({ showModal, id: requestDocId })}
              invalidate={() => queryClient.invalidateQueries(buildClientEntity('leaves', client))}
            />
          </TooltipWrapper>
        );

      return (
        <RejectLeave
          key={`reject-modal-${idx}`}
          leaveId={leaveId}
          buttonText={text}
          buttonInnerText={text}
          buttonSize={null}
          noTooltip
          wrapperClassName="d-block"
          buttonClassName="leaves-modal-action-button"
          onBeforeOpenModal={hideModal}
          handlePreview={() => handlePreviewDoc({ showModal, id: requestDocId })}
          invalidate={() => queryClient.invalidateQueries(buildClientEntity('leaves', client))}
        />
      );
    }

    return null;
  };

  const renderActions = (
    buttons: {
      type: string;
      text: string;
    }[],
    leaveId: number,
    requestDocId: number,
    day: number
  ) => {
    if (!buttons || buttons.length === 0) return null;

    const cellKey = `${leaveId}-${day}`;

    if (isCompactActions) {
      const isActionLoading =
        (isApproving && actionIndex === cellKey) ||
        (isSigning && actionIndex === cellKey) ||
        (isApprovingAndSigning && actionIndex === cellKey);

      return (
        <Button
          key={`compact-actions-${leaveId}-${day}`}
          type="button"
          btnType="icon"
          size="sm"
          className="leaves-grid-action-btn"
          ariaLabel={t('leaves.grid.actionsHeading')}
          disabled={isActionLoading}
          loading={isActionLoading}
          onClick={e => {
            e.stopPropagation();
            showModal({
              type: 'info',
              hideIcon: true,
              heading: t('leaves.grid.actionsHeading'),
              content: (
                <ModalActionsContainer>
                  {buttons.map((button, idx) => renderActionButton('modal', button, idx, leaveId, requestDocId, day))}
                </ModalActionsContainer>
              ),
            });
          }}
        >
          <FaCog size={14} />
        </Button>
      );
    }

    return buttons.map((button, idx) => renderActionButton('grid', button, idx, leaveId, requestDocId, day));
  };

  const getLeaveForDay = (leaves: ILeaveData[], day: number, currentDate: Date): ILeaveData | null => {
    const checkDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    checkDate.setHours(0, 0, 0, 0);

    const leave = leaves.find(l => {
      const leaveStart = new Date(l.date_from);
      leaveStart.setHours(0, 0, 0, 0);

      const leaveEnd = new Date(l.date_to);
      leaveEnd.setHours(0, 0, 0, 0);

      return checkDate >= leaveStart && checkDate <= leaveEnd;
    });

    return leave || null;
  };

  const isLastDayOfLeave = (leave: ILeaveData, day: number, currentDate: Date): boolean => {
    const checkDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), day);
    checkDate.setHours(0, 0, 0, 0);

    const leaveEnd = new Date(leave.date_to);
    leaveEnd.setHours(0, 0, 0, 0);

    const lastDayOfMonth = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    lastDayOfMonth.setHours(0, 0, 0, 0);

    if (leaveEnd <= lastDayOfMonth) {
      return checkDate.getTime() === leaveEnd.getTime();
    }

    return checkDate.getTime() === lastDayOfMonth.getTime();
  };

  const getLeaveColorForDay = (leaves: ILeaveData[], day: number, currentDate: Date): string | null => {
    const leave = getLeaveForDay(leaves, day, currentDate);
    if (!leave) return null;

    const isPending = leave.status === 'pending' || leave.status === 'signing' || leave.status === 'approved';

    const colorMap: { [key: string]: { confirmed: string; pending: string } } = {
      paid: {
        confirmed: 'rgba(50, 191, 163, 1)',
        pending: 'rgba(50, 191, 163, 0.2)',
      },
      unpaid: {
        confirmed: 'rgb(43, 144, 239, 1)',
        pending: 'rgb(43, 144, 239, 0.2)',
      },
      sick: {
        confirmed: 'rgba(255, 184, 34, 1)',
        pending: 'rgba(255, 184, 34, 0.2)',
      },
      additional: {
        confirmed: 'rgba(253, 126, 20, 1)',
        pending: 'rgba(253, 126, 20, 0.2)',
      },
      work_from_home: {
        confirmed: 'rgba(255, 165, 90, 1)',
        pending: 'rgba(255, 165, 90, 0.2)',
      },
      work_from_office: {
        confirmed: 'rgba(184, 86, 4, 1)',
        pending: 'rgba(184, 86, 4, 0.2)',
      },
    };

    const colors = colorMap[leave.type || 'paid'];
    if (!colors) return null;

    return isPending ? colors.pending : colors.confirmed;
  };

  const getLeaveTooltipContent = (leave: ILeaveData): JSX.Element => {
    const leaveType = leave.type === 'additional' ? leave.type_label : t(`leaves.${leave.type}`);
    const leaveStatus = t(`leaves.${leave.status}`);
    const isHalfDay = leave.leave_days === 1;

    return (
      <span>
        {leaveType} {' - '} {leaveStatus}
        {isHalfDay && (
          <>
            {' - '}
            {t('leaves.requestedHalfDay')}
          </>
        )}
        {leave.comment && (
          <>
            <br />
            {t('leaves.comment')}: {leave.comment}
          </>
        )}
      </span>
    );
  };

  const renderCellActions = (leave: ILeaveData, day: number, currentDate: Date): JSX.Element | null => {
    const showActions = leave.buttons && leave.buttons.length > 0 && isLastDayOfLeave(leave, day, currentDate);

    if (!showActions) return null;

    return <ActionsWrapper>{renderActions(leave.buttons!, leave.id, leave.request_document_id!, day)}</ActionsWrapper>;
  };

  const renderCellIcon = (event: IWorkTripData): JSX.Element | null => {
    return (
      <FaSuitcase
        color={getWorkTripsEventStyle(event, true)?.color}
        size={14}
      />
    );
  };

  return (
    <LeavesCalendarGrid
      leavesData={leavesData}
      holidaysData={holidaysData}
      workTripsData={workTripsData}
      date={date}
      onDateChange={onDateChange}
      getLeaveColorForDay={getLeaveColorForDay}
      getLeaveTooltipContent={getLeaveTooltipContent}
      renderCellActions={renderCellActions}
      renderCellIcon={renderCellIcon}
    />
  );
};

export default LeavesGrid;

const ActionsWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 2px;
  pointer-events: none;

  button {
    padding: 2px 4px;
    min-width: auto;
    height: auto;
    pointer-events: auto;
  }

  .leaves-grid-action-btn {
    padding: 2px !important;
  }
`;

const ModalActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;

  .leaves-modal-action-button {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    min-height: 38px;
  }
`;
